import {noop} from "../lib/noop";
import {SinglePopover} from "../lib/bootstrapPopover";

export class SvgView {
	readonly view: SVGElement | null
	readonly viewDefs: SVGDefsElement[] = []

	readonly width: number = 0
	readonly height: number = 0
	x: number = 0
	y: number = 0

	private bindings: { key: string, consumer: (value: string) => void }[] | null = null;

	onClick: () => void = noop

	constructor(view: SVGElement | null) {
		this.view = view

		function getAttributeNumber(view: SVGElement, attr: string, remove: boolean = false) {
			const n = parseFloat(view.getAttribute(attr) as string)
			if (remove) view.removeAttribute(attr)
			return n;
		}

		if (view != null) {
			this.x = getAttributeNumber(view, 'x', true)
			this.y = getAttributeNumber(view, 'y', true)
			this.width = getAttributeNumber(view, 'width')
			this.height = getAttributeNumber(view, 'height')

			if (view.hasAttribute("transform")) {
				const match = /translate\((.+),(.+)\)/.exec(view.getAttribute("transform")!!);
				if (match) {
					this.x = parseFloat(match[1]);
					this.y = parseFloat(match[2]);
				}
			}

			view.addEventListener('click', () => this.onClick())
			view.style.cursor = 'pointer'
		}

		// Все defs надо перевести на корневой уровень. Иначе в firefox оно не заработает
		if (view != null) {
			view.querySelectorAll('defs').forEach(defsNode => {
				this.viewDefs.push(defsNode)
				defsNode.remove();
				view.ownerSVGElement?.prepend(defsNode);
			})
		}

		this.updateLocation();
	}

	dispose() {
		this.viewDefs.forEach(defsNode => {
			defsNode.remove()
		})
		this.view?.remove()
	}

	get centerX() {
		return Math.round(this.x + this.width / 2);
	}

	get centerY() {
		return Math.round(this.y + this.height / 2);
	}

	toX(x: number) {
		this.x = x
		this.updateLocation()
	}

	toCenterX(centerX: number) {
		const offset = this.centerX - this.x;
		this.x = centerX - offset;
		this.updateLocation()
	}

	toY(y: number) {
		this.y = y
		this.updateLocation()
	}

	toggleClass(className: string, on: boolean) {
		const classList = this.view?.classList
		if (!classList) return;

		if (on && !classList.contains(className)) {
			classList.add(className);
		}
		if (!on && classList.contains(className)) {
			classList.remove(className);
		}
	}

	bind(key: string, value: string) {
		this.viewDefs.forEach(defsNode => {
			if (defsNode.parentNode == null) {
				this.view?.ownerSVGElement?.prepend(defsNode);
			}
		})

		const bindings = this.bindLookup();
		bindings
			.filter(b => b.key == key)
			.forEach(b => b.consumer(value))
	}

	bindWith(key: string, consumer: (svg: SVGElement) => void) {
		this.view?.querySelectorAll<SVGElement>(`[data-bind=${key}]`).forEach(consumer);
	}

	private bindLookup(): { key: string, consumer: (text: string) => void }[] {
		if (this.bindings != null) return this.bindings
		this.bindings = [];

		if (!this.view) return []

		function walkChildren(parent: Element, visitor: (element: Element) => void) {
			visitor(parent)
			const children = parent.children;
			for (let i = 0; i < children.length; ++i) {
				const child = children.item(i);
				if (child) walkChildren(child, visitor)
			}
		}

		const bindElement = (parent: Element) => {
			walkChildren(parent, el => {
				const bindValue = el.getAttribute('data-bind');
				if (bindValue != null) {
					this.bindings?.push({key: bindValue, consumer: (value) => el.textContent = value})
				}

				el.getAttributeNames().forEach(attr => {
					if (/^data-bind$/.test(attr)) {
						const key = el.getAttribute(attr);
						if (key) this.bindings?.push({key, consumer: (value) => el.textContent = value})
					}
					const attributeTarget = /^data-bind-(.+)$/.exec(attr)
					if (attributeTarget) {
						const attribute = attributeTarget[1];
						const key = el.getAttribute(attr);
						if (attribute && key) this.bindings?.push({key, consumer: (value) => el.setAttribute(attribute, value)})
					}
				})
			})
		}

		bindElement(this.view);
		this.viewDefs.forEach(defsNode => bindElement(defsNode))

		return this.bindings;
	}

	updateLocation() {
		if (this.view != null) {
			this.view.setAttribute("transform", `translate(${this.x}, ${this.y})`)
		}
		SinglePopover.updateForElement(this.view)
	}
}